// ============================================================================
// NOTIFICATION INBOX SERVICE
// ============================================================================
// Server-side notification inbox — calls the CRS proxy
// (/api/v1/notifications/inbox*) which forwards to notifications-hub-api.
//
// NAMING NOTE: a separate `notificationService` already exists at
// src/services/notificationService.ts — that one is for in-app toast/snackbar
// messaging (ephemeral, client-only). This service is the API client for the
// bell-icon panel (persisted, server-driven).

import { get, post } from '../client/axiosClient';
import { notificationsEndpoints } from '../endpoints';
import type {
  ApiResponse,
  EmptyResponse,
  ID,
  InAppNotification,
  MarkReadRequest,
  NotificationInboxParams,
  NotificationInboxResult,
  NotificationUnreadCountResult,
} from '../types';

class NotificationInboxService {
  /**
   * GET /api/v1/notifications/inbox/access
   * Returns whether the current user's role is allowed to receive notifications.
   * Called once at startup to decide whether to show the bell + start polling.
   */
  async getAccess(): Promise<boolean> {
    const response = await get<ApiResponse<{ enabled: boolean }>>(
      notificationsEndpoints.access(),
    );
    return response.data?.data?.enabled === true;
  }

  /**
   * GET /api/v1/notifications/inbox
   * Returns the current user's in-app inbox (paged).
   */
  async getInbox(params?: NotificationInboxParams): Promise<{
    items: InAppNotification[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const response = await get<ApiResponse<NotificationInboxResult>>(
      notificationsEndpoints.inbox(params),
    );
    const data = response.data?.data;
    return {
      items: data?.items ?? [],
      totalCount: data?.totalCount ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 20,
      totalPages: data?.totalPages ?? 0,
    };
  }

  /**
   * GET /api/v1/notifications/inbox/unread-count
   */
  async getUnreadCount(): Promise<number> {
    const response = await get<ApiResponse<NotificationUnreadCountResult>>(
      notificationsEndpoints.unreadCount(),
    );
    return response.data?.data?.unreadCount ?? 0;
  }

  /**
   * POST /api/v1/notifications/inbox/mark-read
   * Pass `{ channelIds: [...] }` for selective marking, or `{ markAll: true }`.
   */
  async markRead(request: MarkReadRequest): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(
      notificationsEndpoints.markRead(),
      request,
    );
    return response.data;
  }

  /**
   * POST /api/v1/notifications/inbox/{channelId}/archive
   * Removes the notification from the active inbox view.
   */
  async archive(channelId: ID): Promise<EmptyResponse> {
    const response = await post<EmptyResponse>(
      notificationsEndpoints.archive(channelId),
      {},
    );
    return response.data;
  }
}

export const notificationInboxService = new NotificationInboxService();
export default notificationInboxService;
